import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UUID } from "angular2-uuid";
import { Subject } from "rxjs";
import { debounceTime, first, takeUntil } from "rxjs/operators";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { NewBuildingFilterDTO } from "src/app/shared/Models/read/NewBuildingFilterDTO";
import { LoaderService } from "src/app/shared/services/loader.service";
import { TokenService } from "../../auth/token.service";
import { Address } from "../../shared/Models/Address";
import { CreateBuildingDto } from "../../shared/Models/write/CreateBuildingDto";
import { CreateBuildingWithExistingAddressDto } from "../../shared/Models/write/createBuildingWithExistingAddressDto";
import { CreateOutsideSurveyDto } from "../../shared/Models/write/CreateOutsideSurveyDto";
import { BuildingService } from "../building-list/services/building.service";
import { ValidateHouseSuffix } from "src/app/shared/validation/address-validation";
import {
  PostalcodeRegexString,
  HouseNumberRegexString,
} from "src/app/shared/regex/regexes";

@Component({
  selector: "app-create-building-modal",
  templateUrl: "./create-building-modal.component.html",
  styleUrls: ["./create-building-modal.component.scss"],
})
export class CreateBuildingModalComponent implements OnInit, OnDestroy {
  @Input() areaId: string;
  @Output() reloadBuildingList: EventEmitter<any> = new EventEmitter();
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  createBuildingForm: FormGroup;
  matchingExistingAddresses: Address[] = [];
  selectedExistingAdress: Address = null;
  existingAdresses: Address[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  addressInfo: NewBuildingFilterDTO[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public activeModal: NgbActiveModal,
    private buildingService: BuildingService,
    private tokenService: TokenService,
    private loaderService: LoaderService
  ) {}

  ngOnInit() {
    this.createBuildingForm = this.fb.group({
      addressInfo: ["", Validators.required],
      street: ["", Validators.required],
      number: [
        "",
        [Validators.required, Validators.pattern(HouseNumberRegexString)],
      ],
      suffix: new FormControl("", ValidateHouseSuffix),
      postalCode: [
        "",
        [Validators.required, Validators.pattern(PostalcodeRegexString)],
      ],
      city: ["", Validators.required],
      coordinatesX: ["", []],
      coordinatesY: ["", []],
    });

    this.createBuildingForm.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.filterExistingAddresses());

    this.buildingService
      .getAllAddressesWithoutABuilding(this.areaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((addresses) => (this.existingAdresses = addresses));

    this.buildingService
      .getNewBuildingFilterInfo(this.areaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => (this.addressInfo = info));
  }

  onSubmit(startSurvey) {
    if (!this.validateForm()) return;

    const userId = this.tokenService.getAuthUserId();
    if (!userId) return;
    this.loaderService.show();
    var coordinates = [
      {
        posX: this.createBuildingForm.get("coordinatesX").value,
        posY: this.createBuildingForm.get("coordinatesY").value,
        isCoordinatesMiddleBuilding: true,
      },
    ];
    coordinates.some((c) => !c.posX || !c.posX) && (coordinates = []);

    const buildingDTO = this.selectedExistingAdress
      ? new CreateBuildingWithExistingAddressDto({
          ...this.createBuildingForm.value,
          areaId: this.areaId,
          addressId: this.selectedExistingAdress.id,
          coordinates: coordinates,
        })
      : new CreateBuildingDto({
          ...this.createBuildingForm.value,
          areaId: this.areaId,
          coordinates: coordinates,
        });

    const obs = this.selectedExistingAdress
      ? this.buildingService.createBuildingWithExistingAddress(
          buildingDTO as CreateBuildingWithExistingAddressDto
        )
      : this.buildingService.createBuilding(buildingDTO);

    obs.pipe(first()).subscribe(
      (data) => {
        if (startSurvey) {
          this.createOutsideSurvey(data.aggregateId, userId);
          this.activeModal.close();
        } else {
          this.activeModal.close();
        }

        this.reloadBuildingList.emit();
      },
      () => {
        this.loaderService.hide();
      }
    );
  }

  async createOutsideSurvey(buildingId, userId) {
    const dto = new CreateOutsideSurveyDto({
      surveyId: UUID.UUID(),
      buildingId,
      outsideEngineerId: userId,
      isPrefill: false,
    });
    this.buildingService
      .createOutsideSurvey(dto)
      .pipe(first())
      .subscribe(
        (res) => {
          this.router.navigate([
            "/survey",
            SurveyStatus.OutsidePending,
            dto.surveyId,
            false,
          ]);
        },
        (error) => {
          this.router.navigateByUrl("/building-table");
        }
      );
  }

  validateForm(): boolean {
    if (!this.createBuildingForm.valid) {
      Object.keys(this.createBuildingForm.controls).forEach((field) => {
        const control = this.createBuildingForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      return false;
    }
    return true;
  }

  filterExistingAddresses() {
    if (this.formHasValue() == 0) {
      this.matchingExistingAddresses = [];
      return;
    }

    const selectedItem = this.addressInfo.find(
      (i) =>
        this.getAddressInfoKey(i) ===
        this.createBuildingForm.value["addressInfo"]
    );

    var currentNumber = this.createBuildingForm.value["number"];
    var currentSuffix = this.createBuildingForm.value["suffix"];

    this.matchingExistingAddresses = this.existingAdresses.filter((e) => {
      if (!selectedItem) return false;
      return (
        e.street == selectedItem.streetName &&
        e.postalCode == selectedItem.postalCode &&
        e.city == selectedItem.city &&
        (!currentNumber || e.number == currentNumber) &&
        (e.suffix == currentSuffix || !currentSuffix)
      );
    });
    this.selectedExistingAdress = null;
  }

  chooseExistingAdress(adress: Address) {
    this.selectedExistingAdress = adress;
    this.matchingExistingAddresses = [this.selectedExistingAdress];

    this.createBuildingForm.controls["street"].setValue(adress.street, {
      emitEvent: false,
    });
    this.createBuildingForm.controls["city"].setValue(adress.city, {
      emitEvent: false,
    });
    this.createBuildingForm.controls["postalCode"].setValue(
      adress.postalCode.toString(),
      { emitEvent: false }
    );
    this.createBuildingForm.controls["suffix"].setValue(adress.suffix, {
      emitEvent: false,
    });
    this.createBuildingForm.controls["number"].setValue(adress.number, {
      emitEvent: false,
    });
  }

  formHasValue() {
    let cummulativeValue = 0;
    const totalValue = Object.keys(this.createBuildingForm.controls).reduce(
      (cummulative, current) => {
        const val = this.createBuildingForm.controls[current].value;
        if (current == "coordinatesX" || current == "coordinatesY") return 0;

        cummulativeValue = cummulative += val;
        return cummulativeValue;
      },
      0
    );
    return cummulativeValue;
  }

  get showSubmit() {
    let show = this.createBuildingForm.valid;

    if (this.matchingExistingAddresses.length > 0)
      show = !!this.selectedExistingAdress;

    return show;
  }

  getAddressInfoKey(dto: NewBuildingFilterDTO): string {
    return (
      dto.streetName + " (" + dto.postalCode + " - " + (dto.city ?? "TBD") + ")"
    );
  }

  getAddressKey(dto: Address): string {
    return dto.street + " (" + dto.postalCode + " - " + dto.city + ")";
  }

  changeStreet(e) {
    const selectedItem = this.addressInfo.find(
      (i) => this.getAddressInfoKey(i) === e.target.value
    );

    this.createBuildingForm.controls["street"].setValue(
      selectedItem.streetName
    );
    this.createBuildingForm.controls["city"].setValue(selectedItem.city);
    this.createBuildingForm.controls["postalCode"].setValue(
      selectedItem.postalCode.toString()
    );
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.loaderService.hide();
  }
}
